import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Users, GraduationCap, Newspaper, Receipt, Heart, ExternalLink } from "lucide-react";

/** Animated counter — counts up from 0 to `target` in `duration` ms. */
function AnimatedCounter({ target = 0, duration = 1500 }) {
  const [value, setValue] = useState(0);
  const ref = useRef();
  const started = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const start = Date.now();
          const timer = setInterval(() => {
            const elapsed = Date.now() - start;
            const progress = Math.min(elapsed / duration, 1);
            // Ease-out cubic
            const eased = 1 - Math.pow(1 - progress, 3);
            setValue(Math.floor(target * eased));
            if (progress >= 1) clearInterval(timer);
          }, 16);
        }
      });
    }, { threshold: 0.3 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [target, duration]);

  return <span ref={ref}>{value.toLocaleString("fr-FR")}</span>;
}

export default function ImpactLiveSection({ API, lang = "fr" }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/impact/live`).then(r => setData(r.data)).catch(err => console.error(err));
  }, [API]);

  const T = {
    fr: {
      badge: "Impact temps réel",
      title: "SAFUAYE en chiffres — mis à jour en direct",
      subtitle: "Chaque nombre ci-dessous est calculé instantanément depuis notre base transparente. Aucune approximation, aucune simulation.",
      beneficiaries: "Bénéficiaires actifs",
      children: "Enfants au parrainage",
      pledges: "Engagements confirmés",
      transactions: "Transactions tracées",
      press: "Articles de presse",
      press_hdr: "Vus dans la presse",
      updated: "Données actualisées",
      trust1: "Charity Commission (UK)",
      trust2: "Object Storage sécurisé",
      trust3: "Justificatifs signés",
    },
    en: {
      badge: "Live impact",
      title: "SAFUAYE by the numbers — updated live",
      subtitle: "Every number below is computed instantly from our transparent database. No approximations, no simulations.",
      beneficiaries: "Active beneficiaries",
      children: "Sponsored children",
      pledges: "Confirmed pledges",
      transactions: "Traced transactions",
      press: "Press articles",
      press_hdr: "Seen in the press",
      updated: "Data refreshed",
      trust1: "Charity Commission (UK)",
      trust2: "Secure Object Storage",
      trust3: "Signed receipts",
    },
    it: {
      badge: "Impatto in diretta",
      title: "SAFUAYE in numeri — aggiornati in tempo reale",
      subtitle: "Ogni cifra qui sotto è calcolata istantaneamente dal nostro database trasparente.",
      beneficiaries: "Beneficiari attivi",
      children: "Bambini sponsorizzati",
      pledges: "Impegni confermati",
      transactions: "Transazioni tracciate",
      press: "Articoli di stampa",
      press_hdr: "Visti sulla stampa",
      updated: "Dati aggiornati",
      trust1: "Charity Commission (UK)",
      trust2: "Object Storage sicuro",
      trust3: "Ricevute firmate",
    },
  }[lang] || {};
  const t = T;

  if (!data) return null;

  return (
    <section id="impact-live"
             className="py-16 md:py-20 bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 text-white relative overflow-hidden"
             data-testid="impact-live-section">
      {/* Decorative gradient orbs */}
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />

      <div className="relative max-w-6xl mx-auto px-6">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-500/20 border border-emerald-400/40 rounded-full text-xs font-bold uppercase tracking-widest text-emerald-300 mb-4">
            <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
            {t.badge}
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-3">
            {t.title}
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto text-base">
            {t.subtitle}
          </p>
        </div>

        {/* KPI grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white/5 backdrop-blur-sm border border-emerald-400/30 rounded-2xl p-5 text-center hover:bg-white/10 transition-colors"
               data-testid="impact-kpi-beneficiaries">
            <Users className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
            <div className="text-4xl font-bold text-white">
              <AnimatedCounter target={data.beneficiaries_active} />
            </div>
            <div className="text-xs text-emerald-200/80 mt-1 font-semibold uppercase tracking-wider">
              {t.beneficiaries}
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-amber-400/30 rounded-2xl p-5 text-center hover:bg-white/10 transition-colors"
               data-testid="impact-kpi-children">
            <GraduationCap className="w-8 h-8 text-amber-400 mx-auto mb-2" />
            <div className="text-4xl font-bold text-white">
              <AnimatedCounter target={data.sponsored_children} />
            </div>
            <div className="text-xs text-amber-200/80 mt-1 font-semibold uppercase tracking-wider">
              {t.children}
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-pink-400/30 rounded-2xl p-5 text-center hover:bg-white/10 transition-colors"
               data-testid="impact-kpi-pledges">
            <Heart className="w-8 h-8 text-pink-400 mx-auto mb-2" />
            <div className="text-4xl font-bold text-white">
              <AnimatedCounter target={data.active_pledges} />
            </div>
            <div className="text-xs text-pink-200/80 mt-1 font-semibold uppercase tracking-wider">
              {t.pledges}
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-blue-400/30 rounded-2xl p-5 text-center hover:bg-white/10 transition-colors"
               data-testid="impact-kpi-transactions">
            <Receipt className="w-8 h-8 text-blue-400 mx-auto mb-2" />
            <div className="text-4xl font-bold text-white">
              <AnimatedCounter target={data.transactions_recorded} />
            </div>
            <div className="text-xs text-blue-200/80 mt-1 font-semibold uppercase tracking-wider">
              {t.transactions}
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-purple-400/30 rounded-2xl p-5 text-center hover:bg-white/10 transition-colors"
               data-testid="impact-kpi-press">
            <Newspaper className="w-8 h-8 text-purple-400 mx-auto mb-2" />
            <div className="text-4xl font-bold text-white">
              <AnimatedCounter target={data.press_articles_count} />
            </div>
            <div className="text-xs text-purple-200/80 mt-1 font-semibold uppercase tracking-wider">
              {t.press}
            </div>
          </div>
        </div>

        {/* Press articles quick view */}
        {data.press_articles?.length > 0 && (
          <div className="mt-10">
            <h3 className="text-sm font-bold uppercase tracking-widest text-emerald-300 mb-3 flex items-center gap-2">
              <Newspaper className="w-4 h-4" />
              {t.press_hdr}
            </h3>
            <div className="grid md:grid-cols-2 gap-3">
              {data.press_articles.map((art, i) => (
                <a key={i}
                   href={art.url || "#presse"}
                   target={art.url ? "_blank" : "_self"}
                   rel="noopener noreferrer"
                   className="group bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-4 transition-colors flex items-start gap-3"
                   data-testid={`impact-press-article-${i}`}>
                  <div className="text-xs shrink-0 text-emerald-300 font-mono pt-0.5">{art.date}</div>
                  <div className="flex-1">
                    <div className="text-xs uppercase font-semibold text-amber-300 mb-1">{art.media}</div>
                    <div className="text-sm text-white/90 group-hover:text-amber-200 transition-colors leading-snug">
                      {art.title}
                    </div>
                  </div>
                  {art.url && <ExternalLink className="w-4 h-4 text-white/40 shrink-0 group-hover:text-amber-300 transition-colors mt-1" />}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Trust bar */}
        <div className="mt-10 flex flex-wrap justify-center gap-4 text-xs text-white/60">
          <span className="flex items-center gap-1">🇬🇧 {t.trust1}</span>
          <span>•</span>
          <span>🔒 {t.trust2}</span>
          <span>•</span>
          <span>✍️ {t.trust3}</span>
        </div>

        {data.generated_at && (
          <p className="text-center text-xs text-white/40 mt-4">
            {t.updated} : {new Date(data.generated_at).toLocaleString(lang === "en" ? "en-GB" : lang === "it" ? "it-IT" : "fr-FR")}
          </p>
        )}
      </div>
    </section>
  );
}
